/**
 * Draws a wrapped recap as one portrait image (1080x1350, the Instagram/WhatsApp
 * friendly 4:5) and shares it with the native share sheet, or downloads it where
 * file sharing isn't supported. Plain canvas, no extra dependency.
 */
import { WrapCard, WrapPic } from './wrappedCards';

/** Loaded pictures by URL; null when one couldn't be loaded (initials are drawn instead). */
export type WrapImageMap = Map<string, HTMLImageElement | null>;

const IMAGE_TIMEOUT_MS = 5000;

// A canvas with a cross-origin picture can't be exported, so pictures load with
// CORS. The extra query parameter keeps a copy the browser cached without CORS
// headers (from an <img> elsewhere in the app) from being reused here.
const corsUrl = (url: string) => `${url}${url.includes('?') ? '&' : '?'}share=1`;

const loadImage = (url: string) =>
  new Promise<HTMLImageElement | null>(resolve => {
    const img = new Image();
    const timer = setTimeout(() => resolve(null), IMAGE_TIMEOUT_MS);
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      clearTimeout(timer);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(null);
    };
    img.src = corsUrl(url);
  });

/** Every picture the share image draws, loaded in parallel. */
export const loadWrapImages = async (cards: WrapCard[]): Promise<WrapImageMap> => {
  const urls = new Set<string>();
  cards.forEach(card => [...card.pics, ...(card.row || [])].forEach(pic => pic.url && urls.add(pic.url)));
  const list = Array.from(urls);
  const loaded = await Promise.all(list.map(loadImage));
  return new Map(list.map((url, i) => [url, loaded[i]]));
};

const W = 1080;
const H = 1350;
const PAD = 64;
const ORANGE = '#c2410c'; // primary-700
const CREAM = '#fff7ed'; // primary-50
const BORDER = '#fdba74'; // primary-300
const TEXT = '#1f2937';
const MUTED = '#4b5563';
const GREEN = '#15803d';
const FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
// PlayerAvatar's initials colours
const AVATAR_COLORS = ['#dc2626', '#b45309', '#15803d', ORANGE, '#4f46e5', '#9333ea', '#be185d'];

const roundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

/** Text cut with an ellipsis to fit maxWidth. */
const fit = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number) => {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxWidth) t = t.slice(0, -1);
  return `${t}…`;
};

/** Up to `lines` lines of word-wrapped text; the last one ellipsised. */
const wrapLines = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number, lines: number) => {
  const words = text.split(' ');
  const out: string[] = [];
  let line = '';
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width <= maxWidth || !line) line = next;
    else {
      out.push(line);
      line = word;
    }
  }
  if (line) out.push(line);
  if (out.length > lines) {
    out.length = lines;
    out[lines - 1] = fit(ctx, `${out[lines - 1]} …`, maxWidth);
  }
  return out.map(l => fit(ctx, l, maxWidth));
};

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();

// Same hash as PlayerAvatar, so a player keeps their colour
const avatarColor = (name: string) => {
  let hash = 2166136261;
  for (let i = 0; i < name.length; i++) {
    hash ^= name.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return AVATAR_COLORS[(hash >>> 0) % AVATAR_COLORS.length];
};

/** Draws the image cropped to fill the box (like object-fit: cover). */
const drawCover = (ctx: CanvasRenderingContext2D, img: HTMLImageElement, x: number, y: number, w: number, h: number) => {
  const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight);
  const sw = w / scale;
  const sh = h / scale;
  ctx.drawImage(img, (img.naturalWidth - sw) / 2, (img.naturalHeight - sh) / 2, sw, sh, x, y, w, h);
};

/** One picture: players as circles, games and photos as rounded squares; initials when not loaded. */
const drawPic = (
  ctx: CanvasRenderingContext2D,
  pic: WrapPic,
  images: WrapImageMap,
  x: number,
  y: number,
  size: number,
) => {
  const img = pic.url ? images.get(pic.url) : null;
  const round = pic.kind === 'player';
  const ring = Math.max(3, Math.round(size / 24));
  ctx.save();
  if (round) {
    ctx.beginPath();
    ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
  } else {
    roundRect(ctx, x, y, size, size, size / 6);
  }
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.clip();
  if (img) {
    drawCover(ctx, img, x, y, size, size);
  } else {
    ctx.fillStyle = round ? avatarColor(pic.name) : '#ffedd5';
    ctx.fillRect(x, y, size, size);
    ctx.fillStyle = round ? '#ffffff' : '#9a3412';
    ctx.font = `bold ${Math.round(size * 0.38)}px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials(pic.name), x + size / 2, y + size / 2 + 2);
  }
  ctx.restore();
  // White ring so overlapping pictures stay apart
  ctx.save();
  ctx.lineWidth = ring;
  ctx.strokeStyle = '#ffffff';
  if (round) {
    ctx.beginPath();
    ctx.arc(x + size / 2, y + size / 2, size / 2 - ring / 2, 0, Math.PI * 2);
  } else {
    roundRect(ctx, x + ring / 2, y + ring / 2, size - ring, size - ring, size / 6);
  }
  ctx.stroke();
  ctx.restore();
};

export const drawWrappedImage = (
  canvas: HTMLCanvasElement,
  title: string,
  cards: WrapCard[],
  images: WrapImageMap = new Map(),
): void => {
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.fillStyle = CREAM;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = ORANGE;
  ctx.fillRect(0, 0, W, 150);
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold 64px ${FONT}`;
  ctx.textBaseline = 'middle';
  ctx.fillText(fit(ctx, `${title} wrapped`, W - 2 * PAD), PAD, 78);
  ctx.textBaseline = 'alphabetic';

  const [hero, ...rest] = cards;
  let y = 172;
  if (hero) {
    // The group card: what was played and who played, as a row of covers and faces
    const heroH = 262;
    roundRect(ctx, PAD, y, W - 2 * PAD, heroH, 32);
    ctx.fillStyle = ORANGE;
    ctx.fill();
    ctx.fillStyle = '#ffedd5';
    ctx.font = `36px ${FONT}`;
    ctx.fillText(hero.label, PAD + 44, y + 60);
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold 64px ${FONT}`;
    ctx.fillText(fit(ctx, hero.value, W - 2 * PAD - 88), PAD + 44, y + 130);
    const size = 88;
    let px = PAD + 44;
    hero.pics.slice(0, 5).forEach(pic => {
      drawPic(ctx, pic, images, px, y + 150, size);
      px += size + 12;
    });
    px += 18;
    // As many overlapping faces as fit before the card's right edge
    const room = Math.floor((W - PAD - 32 - px - size) / (size - 26)) + 1;
    const faces = (hero.row || []).slice(0, Math.max(0, room));
    if (faces.length > 0) {
      faces.forEach(pic => {
        drawPic(ctx, pic, images, px, y + 150, size);
        px += size - 26;
      });
    }
    y += heroH + 28;
  }

  // Two-column grid; a lone last card spans the width
  const gap = 28;
  const colW = (W - 2 * PAD - gap) / 2;
  const boxH = 232;
  const shown = rest.slice(0, 6);
  shown.forEach((card, i) => {
    const wide = i === shown.length - 1 && shown.length % 2 === 1;
    const x = wide || i % 2 === 0 ? PAD : PAD + colW + gap;
    const top = y + Math.floor(i / 2) * (boxH + gap);
    const w = wide ? W - 2 * PAD : colW;
    if (top + boxH > H - 110) return;
    roundRect(ctx, x, top, w, boxH, 28);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.strokeStyle = BORDER;
    ctx.lineWidth = 4;
    ctx.stroke();
    // Picture on the left (with the first small one as a badge), text beside it
    const size = 104;
    const main = card.pics[0];
    const textX = main ? x + 32 + size + 24 : x + 36;
    const textW = x + w - 32 - textX;
    if (main) {
      drawPic(ctx, main, images, x + 32, top + 32, size);
      const badge = card.row?.[0];
      if (badge) drawPic(ctx, badge, images, x + 32 + size - 40, top + 32 + size - 40, 56);
    }
    ctx.fillStyle = MUTED;
    ctx.font = `600 26px ${FONT}`;
    ctx.fillText(fit(ctx, card.label.toUpperCase(), textW), textX, top + 62);
    ctx.fillStyle = TEXT;
    ctx.font = `bold 40px ${FONT}`;
    const lines = wrapLines(ctx, card.value, textW, 2);
    lines.forEach((line, li) => ctx.fillText(line, textX, top + 112 + li * 46));
    if (card.detail) {
      ctx.fillStyle = card.tone === 'up' ? GREEN : MUTED;
      ctx.font = `30px ${FONT}`;
      ctx.fillText(fit(ctx, card.detail, w - 64), x + 32, top + boxH - 34);
    }
  });

  ctx.fillStyle = MUTED;
  ctx.font = `30px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.fillText('boardgamestats.lorenzotoso.de', W / 2, H - 50);
  ctx.textAlign = 'left';
};

const toBlob = (canvas: HTMLCanvasElement) =>
  new Promise<Blob | null>(resolve => {
    try {
      canvas.toBlob(resolve, 'image/png');
    } catch {
      resolve(null); // a picture without CORS tainted the canvas
    }
  });

/**
 * Shares the recap image via the native share sheet when the browser can share
 * files (phones), else downloads it. Returns what happened, for the button's feedback.
 */
export const shareWrappedImage = async (
  title: string,
  cards: WrapCard[],
  fileName: string,
  images: WrapImageMap = new Map(),
): Promise<'shared' | 'downloaded' | 'cancelled' | 'failed'> => {
  const canvas = document.createElement('canvas');
  drawWrappedImage(canvas, title, cards, images);
  // Fall back to initials only if a picture still tainted the canvas
  let blob = await toBlob(canvas);
  if (!blob && images.size > 0) {
    drawWrappedImage(canvas, title, cards);
    blob = await toBlob(canvas);
  }
  if (!blob) return 'failed';
  const file = new File([blob], fileName, { type: 'image/png' });
  const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
  if (nav.share && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: `${title} wrapped` });
      return 'shared';
    } catch (err) {
      return (err as Error)?.name === 'AbortError' ? 'cancelled' : 'failed';
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return 'downloaded';
};
